import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SLIDES } from "@/components/slides";

const TOTAL = SLIDES.length;

export default function PitchDeck() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback(
    (next) => {
      if (next < 0 || next > TOTAL - 1) return;
      setDirection(next > index ? 1 : -1);
      setIndex(next);
    },
    [index]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        goTo(0);
      } else if (e.key === "End") {
        goTo(TOTAL - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, goTo]);

  const Slide = SLIDES[index].component;
  const atStart = index === 0;
  const atEnd = index === TOTAL - 1;

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 32 : -32 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -32 : 32 }),
  };

  return (
    <div
      className="relative w-screen h-screen bg-white overflow-hidden"
      data-testid="pitch-deck-root"
    >
      {/* Slide stage */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={index}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 flex items-center justify-center px-24 py-14 overflow-y-auto"
          data-testid={`slide-${index + 1}`}
        >
          <Slide />
        </motion.div>
      </AnimatePresence>

      {/* Bottom-left Scout reticle mark (on every slide) */}
      <ScoutMark />

      {/* Top-right slide title chip */}
      <div className="absolute top-8 right-8 z-30 select-none">
        <span
          className="font-mono text-[11px] tracking-[0.18em] text-neutral-500 uppercase"
          data-testid="slide-title-chip"
        >
          {String(index + 1).padStart(2, "0")} — {SLIDES[index].title}
        </span>
      </div>

      {/* Left arrow */}
      <ArrowButton
        direction="left"
        onClick={prev}
        disabled={atStart}
        testId="prev-arrow"
      />
      {/* Right arrow */}
      <ArrowButton
        direction="right"
        onClick={next}
        disabled={atEnd}
        testId="next-arrow"
      />

      {/* Bottom counter + dots */}
      <div className="absolute bottom-8 left-0 right-0 z-30 flex flex-col items-center gap-4 px-8">
        <div className="flex items-center gap-2" data-testid="slide-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.title}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`slide-dot-${i + 1}`}
              className="group relative h-2 flex items-center"
            >
              <span
                className={`block h-[3px] transition-all duration-500 ${
                  i === index
                    ? "w-10 bg-[var(--scout-blue)]"
                    : "w-4 bg-neutral-300 group-hover:bg-neutral-500"
                }`}
              />
            </button>
          ))}
        </div>
        <div
          className="font-mono text-[11px] tracking-[0.18em] text-neutral-400 uppercase"
          data-testid="slide-counter"
        >
          <span className="text-neutral-900">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="mx-2">/</span>
          <span>{String(TOTAL).padStart(2, "0")}</span>
        </div>
      </div>
    </div>
  );
}

function ScoutMark() {
  return (
    <div
      className="absolute bottom-8 left-8 z-30 flex items-center gap-3 select-none"
      data-testid="scout-mark"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Scout Platform"
      >
        {/* outer ring */}
        <circle
          cx="20"
          cy="20"
          r="13"
          stroke="var(--scout-blue)"
          strokeWidth="2"
        />
        {/* mid ring */}
        <circle
          cx="20"
          cy="20"
          r="8"
          stroke="var(--scout-blue)"
          strokeWidth="2"
        />
        {/* center dot */}
        <circle cx="20" cy="20" r="2.2" fill="var(--scout-blue)" />
        {/* crosshair ticks */}
        <line
          x1="20"
          y1="0"
          x2="20"
          y2="5"
          stroke="var(--scout-blue)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="20"
          y1="35"
          x2="20"
          y2="40"
          stroke="var(--scout-blue)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="0"
          y1="20"
          x2="5"
          y2="20"
          stroke="var(--scout-blue)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="35"
          y1="20"
          x2="40"
          y2="20"
          stroke="var(--scout-blue)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <span className="font-mono text-[10px] tracking-[0.22em] text-neutral-400 uppercase">
        Scout Platform
      </span>
      <span className="font-mono text-[10px] tracking-[0.22em] text-neutral-300">
        ×
      </span>
      <span className="font-mono text-[10px] tracking-[0.22em] text-neutral-400 uppercase">
        Fortis.io
      </span>
    </div>
  );
}

function ArrowButton({ direction, onClick, disabled, testId }) {
  const isLeft = direction === "left";
  const Icon = isLeft ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={isLeft ? "Previous slide" : "Next slide"}
      className={`group absolute top-1/2 -translate-y-1/2 z-30 ${
        isLeft ? "left-6" : "right-6"
      } flex items-center justify-center
        h-20 w-20 md:h-24 md:w-24
        bg-white border-[3px] border-neutral-900 rounded-full
        shadow-[6px_6px_0_0_#0a0a0a] active:shadow-[2px_2px_0_0_#0a0a0a]
        active:translate-x-[2px] active:translate-y-[calc(-50%+2px)]
        transition-all duration-150 ease-out
        hover:bg-[var(--scout-blue)] hover:text-white hover:border-[var(--scout-blue)]
        disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-neutral-900 disabled:hover:border-neutral-900`}
    >
      <Icon
        strokeWidth={3}
        className="h-10 w-10 md:h-12 md:w-12 transition-transform duration-300 group-hover:scale-110 group-disabled:group-hover:scale-100"
      />
    </button>
  );
}
